import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

import { PageLayout } from '@/components/poof-ui/PageLayout';

interface LegalPageProps {
  title: string;
  content: string;
}

interface MarkdownBlock {
  type: 'heading' | 'paragraph' | 'list';
  level?: number;
  content?: string;
  items?: string[];
  ordered?: boolean;
}

interface ParsedListItem {
  ordered: boolean;
  items: string[];
}

function pushListBlock(blocks: MarkdownBlock[], listState: ParsedListItem) {
  if (listState.items.length > 0) {
    blocks.push({
      type: 'list',
      ordered: listState.ordered,
      items: [...listState.items],
    });
  }
}

function createInlineMarkup(text: string) {
  const parts = text.split(/(\[[^\]]+\]\([^\)]+\)|\*\*[^*]+\*\*)/g).filter(Boolean);

  return parts.map((part, index) => {
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^\)]+)\)$/);
    if (linkMatch) {
      return (
        <a
          key={`${part}-${index}`}
          href={linkMatch[2]}
          target='_blank'
          rel='noreferrer'
          className='text-primary underline underline-offset-4 transition-colors hover:text-primary/80'
        >
          {linkMatch[1]}
        </a>
      );
    }

    const boldMatch = part.match(/^\*\*([^*]+)\*\*$/);
    if (boldMatch) {
      return (
        <strong key={`${part}-${index}`} className='font-semibold text-foreground'>
          {boldMatch[1]}
        </strong>
      );
    }

    return <span key={`${part}-${index}`}>{part}</span>;
  });
}

function parseMarkdown(markdown: string): MarkdownBlock[] {
  const lines = markdown.split(/\r?\n/);
  const blocks: MarkdownBlock[] = [];
  let paragraphLines: string[] = [];
  let listState: ParsedListItem = { ordered: false, items: [] };

  const flushParagraph = () => {
    if (paragraphLines.length > 0) {
      blocks.push({
        type: 'paragraph',
        content: paragraphLines.join(' '),
      });
      paragraphLines = [];
    }
  };

  const flushList = () => {
    pushListBlock(blocks, listState);
    listState = { ordered: false, items: [] };
  };

  lines.forEach((rawLine) => {
    const line = rawLine.trim();

    if (!line) {
      flushParagraph();
      flushList();
      return;
    }

    const headingMatch = line.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      flushParagraph();
      flushList();
      blocks.push({
        type: 'heading',
        level: headingMatch[1].length,
        content: headingMatch[2],
      });
      return;
    }

    const listMatch = line.match(/^[-*]\s+(.*)$/);
    if (listMatch) {
      flushParagraph();
      if (listState.items.length > 0 && listState.ordered) {
        flushList();
      }
      listState.ordered = false;
      listState.items.push(listMatch[1]);
      return;
    }

    const orderedListMatch = line.match(/^\d+\.\s+(.*)$/);
    if (orderedListMatch) {
      flushParagraph();
      if (listState.items.length > 0 && !listState.ordered) {
        flushList();
      }
      listState.ordered = true;
      listState.items.push(orderedListMatch[1]);
      return;
    }

    if (listState.items.length > 0) {
      flushList();
    }

    paragraphLines.push(line);
  });

  flushParagraph();
  flushList();

  return blocks;
}

export default function LegalPage({ title, content }: LegalPageProps) {
  const blocks = parseMarkdown(content);

  return (
    <PageLayout footer={false}>
      <div className='mx-auto flex w-full max-w-4xl flex-col gap-8 px-4 py-6 sm:px-6 lg:px-0'>
        <div className='flex flex-col gap-4'>
          <Link
            to='/'
            className='inline-flex w-fit items-center gap-2 rounded-full border border-border/60 bg-card/70 px-4 py-2 text-xs font-medium uppercase tracking-[0.24em] text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground'
          >
            <ArrowLeft className='h-4 w-4' />
            Back to home
          </Link>

          <div className='space-y-3 border-b border-border/60 pb-6'>
            <p className='text-xs font-semibold uppercase tracking-[0.32em] text-primary/80'>SeekScape legal</p>
            <h1 className='text-3xl font-semibold tracking-tight text-foreground sm:text-4xl'>
              {title}
            </h1>
            <p className='max-w-2xl text-sm leading-7 text-muted-foreground sm:text-base'>
              The following information governs your use of SeekScape and its wallpaper marketplace.
            </p>
          </div>
        </div>

        <article className='rounded-3xl border border-border/60 bg-card/40 p-6 shadow-[0_24px_80px_rgba(0,0,0,0.24)] backdrop-blur-sm sm:p-8'>
          <div className='space-y-5 text-sm leading-7 text-foreground/88 sm:text-base'>
            {blocks.map((block, index) => {
              if (block.type === 'heading') {
                const HeadingTag = block.level === 1 ? 'h2' : block.level === 2 ? 'h3' : 'h4';
                const headingClassName =
                  block.level === 1
                    ? 'text-2xl font-semibold tracking-tight text-foreground'
                    : block.level === 2
                      ? 'pt-3 text-xl font-semibold tracking-tight text-foreground'
                      : 'pt-2 text-base font-semibold uppercase tracking-[0.12em] text-primary/85';

                return (
                  <HeadingTag key={`${block.type}-${index}`} className={headingClassName}>
                    {createInlineMarkup(block.content ?? '')}
                  </HeadingTag>
                );
              }

              if (block.type === 'list') {
                const ListTag = block.ordered ? 'ol' : 'ul';

                return (
                  <ListTag
                    key={`${block.type}-${index}`}
                    className='space-y-3 pl-5 text-foreground/82 marker:text-primary'
                  >
                    {(block.items ?? []).map((item, itemIndex) => (
                      <li key={`${item}-${itemIndex}`}>{createInlineMarkup(item)}</li>
                    ))}
                  </ListTag>
                );
              }

              return (
                <p
                  key={`${block.type}-${index}`}
                  className='text-foreground/78'
                >
                  {createInlineMarkup(block.content ?? '')}
                </p>
              );
            })}
          </div>
        </article>
      </div>
    </PageLayout>
  );
}
